import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Pencil, ListChecks, Wallet, FileText, Landmark, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui-kit/Button";
import { Card } from "@/components/ui-kit/Card";
import { MatterHeader } from "@/components/workbench/MatterHeader";
import { EntryGrid } from "@/components/workbench/EntryGrid";
import { BillingStrip } from "@/components/workbench/BillingStrip";
import { WorkbenchAlertStrip } from "@/components/workbench/WorkbenchAlertStrip";
import { TemplateManagerDialog } from "@/components/templates/TemplateManagerDialog";
import { InvoiceReviewDialog } from "@/components/invoices/InvoiceReviewDialog";
import { InvoiceHistory } from "@/components/invoices/InvoiceHistory";
import { CostsPanel } from "@/components/costs/CostsPanel";
import { PaymentDialog } from "@/components/payments/PaymentDialog";
import { PaymentHistory } from "@/components/payments/PaymentHistory";
import { LedgerStatement } from "@/components/ledger/LedgerStatement";
import { useFunder } from "@/funder/FunderContext";

export function MatterWorkbenchPage() {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { abbr } = useFunder();
  const [matter, setMatter] = useState(null);
  const [clientName, setClientName] = useState("");
  const [retainerBalance, setRetainerBalance] = useState(0);
  const [presets, setPresets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [version, setVersion] = useState(0);
  const [tplOpen, setTplOpen] = useState(false);
  const [reviewPayer, setReviewPayer] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [unbilled, setUnbilled] = useState({ client: 0, acsa: 0 });

  const loadMatter = () =>
    api.get(`/matters/${matterId}`).then((r) => {
      setMatter(r.data);
      api.get(`/clients/${r.data.client_id}`).then((c) => setClientName(c.data.name)).catch(() => {});
      api.get(`/clients/${r.data.client_id}/retainer`).then((rt) => setRetainerBalance(rt.data.balance)).catch(() => {});
    }).catch(() => {});
  const loadTemplates = () => api.get("/activity-templates").then((r) => setTemplates(r.data)).catch(() => {});

  useEffect(() => {
    loadMatter();
    api.get("/settings").then((r) => setPresets(r.data.quick_hour_presets || [])).catch(() => {});
    loadTemplates();
  }, [matterId]);

  // Live unbilled (approved, not yet invoiced) amounts per payer — drive the
  // unbilled-amount badges on the Create-invoice buttons. Refresh on every bump.
  useEffect(() => {
    let active = true;
    Promise.all([
      api.get(`/matters/${matterId}/ledger?payer=Client`),
      api.get(`/matters/${matterId}/ledger?payer=ACSA`),
    ])
      .then(([lc, la]) => {
        if (!active) return;
        setUnbilled({
          client: Math.max(0, lc.data.unbilled_work || 0),
          acsa: Math.max(0, la.data.unbilled_work || 0),
        });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [matterId, version]);

  // Deep-link from the dashboard notifications: ?invoice=ACSA|Client auto-opens
  // the matching invoice review dialog, then strips the param so it won't reopen.
  useEffect(() => {
    if (!matter) return;
    const inv = searchParams.get("invoice");
    if (!inv) return;
    const openFromLink = () => {
      if (inv === "ACSA" && matter.type === "ACSA_Client") setReviewPayer("ACSA");
      else if (inv === "Client") setReviewPayer("Client");
      const next = new URLSearchParams(searchParams);
      next.delete("invoice");
      setSearchParams(next, { replace: true });
    };
    openFromLink();
  }, [matter, searchParams, setSearchParams]);

  const bump = () => setVersion((v) => v + 1);

  if (!matter) {
    return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  const isAcsa = matter.type === "ACSA_Client";
  const matterForGrid = {
    id: matter.id,
    hourly_rate: matter.hourly_rate,
    billing_increment_min: matter.billing_increment_min,
    require_time_review: matter.require_time_review,
  };
  const r1 = (matter.coverage_rounds && matter.coverage_rounds[0]) || {};
  const matterForHeader = {
    id: matter.id, name: matter.name, type: matter.type, status: matter.status,
    hourly_rate: matter.hourly_rate, billing_increment_min: matter.billing_increment_min,
    applied_credit: matter.applied_credit, retainerBalance, require_time_review: matter.require_time_review,
    levels: { l1: !!r1.l1_enabled, l2: !!r1.l2_enabled, l3: !!r1.l3_enabled },
  };

  const extraActions = (
    <>
      <Button size="sm" onClick={() => setReviewPayer("Client")} data-testid="create-client-invoice">
        <FileText className="h-4 w-4" /> Create Client invoice
        {unbilled.client > 0.005 && (
          <span
            className="ml-1 rounded-full bg-primary-foreground/20 px-1.5 py-0.5 text-[11px] font-semibold nums"
            data-testid="unbilled-badge-client"
          >
            {money(unbilled.client)}
          </span>
        )}
      </Button>
      {isAcsa && (
        <Button size="sm" onClick={() => setReviewPayer("ACSA")} data-testid="create-acsa-invoice">
          <Landmark className="h-4 w-4" /> Create {abbr} invoice
          {unbilled.acsa > 0.005 && (
            <span
              className="ml-1 rounded-full bg-primary-foreground/20 px-1.5 py-0.5 text-[11px] font-semibold nums"
              data-testid="unbilled-badge-acsa"
            >
              {money(unbilled.acsa)}
            </span>
          )}
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => navigate(`/matters/${matter.id}/edit`)} data-testid="edit-matter">
        <Pencil className="h-4 w-4" /> Edit matter
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setTplOpen(true)} data-testid="manage-templates">
        <ListChecks className="h-4 w-4" /> Manage templates
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setPayOpen(true)} data-testid="add-payment">
        <Wallet className="h-4 w-4" /> Add payment
      </Button>
      <Button size="sm" variant="ghost" onClick={() => navigate(`/matters/${matter.id}/statement`)} data-testid="view-statement">
        <Receipt className="h-4 w-4" /> View statement
      </Button>
    </>
  );

  return (
    <div className="space-y-4 pb-4" data-testid="matter-workbench-page">
      <MatterHeader matter={matterForHeader} clientName={clientName} clientId={matter.client_id} search={search} onSearch={setSearch} onChanged={() => { loadMatter(); bump(); }} />

      <WorkbenchAlertStrip matterId={matter.id} version={version} onAction={bump} />

      <BillingStrip matterId={matter.id} version={version} isAcsa={isAcsa} rate={matter.hourly_rate} />

      <Card className="p-4">
        <EntryGrid
          matterId={matter.id}
          matter={matterForGrid}
          presets={presets}
          templates={templates}
          search={search}
          onChanged={bump}
          extraActions={extraActions}
        />
      </Card>

      <InvoiceHistory matterId={matter.id} version={version} />

      <CostsPanel matterId={matter.id} version={version} />

      <PaymentHistory matterId={matter.id} version={version} onChanged={bump} />

      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Reconciliation statement</h3>
          <Button size="sm" variant="ghost" onClick={() => navigate(`/matters/${matter.id}/statement`)} data-testid="open-full-statement">
            <Receipt className="h-4 w-4" /> Open full statement
          </Button>
        </div>
        <LedgerStatement key={version} matterId={matter.id} />
      </Card>

      <TemplateManagerDialog open={tplOpen} onOpenChange={setTplOpen} onChanged={loadTemplates} />
      <InvoiceReviewDialog
        open={reviewPayer !== null}
        onOpenChange={(o) => { if (!o) setReviewPayer(null); }}
        matterId={matter.id}
        payer={reviewPayer}
        onDone={bump}
      />
      <PaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        matterId={matter.id}
        clientId={matter.client_id}
        defaultPayer="Client"
        onDone={bump}
      />
    </div>
  );
}
